import { TaxRateLiterals } from "@graffer-inc/cloud-gov-core";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  GetQualifiedInvoiceDetailPaymentQuery,
  GetQualifiedInvoiceIssueRequestQuery,
  IssueQualifiedInvoiceInput,
  gqlClient,
  gqlHeader,
  reportUnexpectedError,
  runGqlQueryV2,
} from "@graffer-inc/smart-apply";
import {
  qualifiedInvoiceDetailPaymentTaxAmountDetailType,
  qualifiedInvoiceDetailPaymentTaxAmountDetailErrorType,
  qualifiedInvoiceFormatType,
} from "../../../../../application/domain/qualified-invoice";
import {
  ProcedureEntity,
  ProcedureType,
} from "../../../../../application/domain/procedures/procedure-entity";
import {
  ApplicationEntity,
  ApplicationType,
} from "../../../../../application/domain/application/application-entity";

type Props = {
  id: string;
};

type redirectToType = "application-detail";

const qualifiedInvoiceBuyerNameMaxLength = 255;
const qualifiedInvoiceBuyerNameDefaultSuffix = "　様";

export const useQualifiedInvoiceNewContainer = ({ id }: Props) => {
  const [redirectTo, setRedirectTo] = useState<redirectToType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [procedureId, setProcedureId] = useState<string>("");
  const [procedureGroupId, setProcedureGroupId] = useState<string>("");
  const [issueRequest, setIssueRequest] =
    useState<
      GetQualifiedInvoiceIssueRequestQuery["qualifiedInvoiceIssueRequest"]
    >(null);

  const [detailPayment, setDetailPayment] =
    useState<
      GetQualifiedInvoiceDetailPaymentQuery["qualifiedInvoiceDetailPayment"]
    >(null);
  const [amountDetail, setAmountDetail] =
    useState<qualifiedInvoiceDetailPaymentTaxAmountDetailType>({});

  const handleOnRedirectToApplicationDetail = useCallback(() => {
    setRedirectTo("application-detail");
  }, []);

  const [format, setFormat] =
    useState<qualifiedInvoiceFormatType>("SIMPLIFIED");

  const handleOnChangeFormat = (value: string) => {
    setFormat(value as qualifiedInvoiceFormatType);
  };

  const [transactionDate, setTransactionDate] = useState<Date>(
    dayjs().toDate(),
  );
  const [transactionDateError, setTransactionDateError] = useState("");

  const handleOnChangeTransactionDate = (value: Date) => {
    setTransactionDate(value);
    if (dayjs().toDate() < value) {
      setTransactionDateError("今日以前の日付を設定してください。");
    } else {
      setTransactionDateError("");
    }
  };

  const [buyerName, setBuyerName] = useState("");
  const [buyerNameApplicantName, setBuyerNameApplicantName] = useState("");
  const [buyerNameError, setBuyerNameError] = useState("");

  const handleOnChangeBuyerName = (value: string) => {
    if (!value) {
      setBuyerNameError(`入力してください。`);
    } else if (!value.trim()) {
      setBuyerNameError(`空白以外を入力してください。`);
    } else if (value.length > qualifiedInvoiceBuyerNameMaxLength) {
      setBuyerNameError(
        `${qualifiedInvoiceBuyerNameMaxLength}文字以内で入力してください。`,
      );
    } else {
      setBuyerNameError("");
    }

    setBuyerName(value);
  };

  const [taxAmountDetail, setTaxAmountDetail] =
    useState<qualifiedInvoiceDetailPaymentTaxAmountDetailType>({});
  const [taxAmountDetailError, setTaxAmountDetailError] =
    useState<qualifiedInvoiceDetailPaymentTaxAmountDetailErrorType>({});

  const handleOnChangeTaxAmountDetail = ({
    taxRate,
    value,
  }: {
    taxRate: TaxRateLiterals;
    value: number;
  }) => {
    const isIntInput = !Number.isNaN(value) && value >= 0;

    setTaxAmountDetail({
      ...taxAmountDetail!,
      [taxRate]: isIntInput ? value : 0,
    });

    const isOverAmount = value >= detailPayment?.amountDetail[taxRate];

    setTaxAmountDetailError({
      ...taxAmountDetailError,
      [taxRate]:
        isIntInput && isOverAmount
          ? `${detailPayment?.amountDetail[taxRate]}円未満の金額を入力してください。`
          : "", // 不正な値の場合は0をセットするので、バリデーションを解除しておく
    });
  };

  const disabledIssueButton =
    format === "SIMPLIFIED"
      ? !!transactionDateError
      : !!transactionDateError ||
        !!buyerNameError ||
        !!taxAmountDetailError["0.1"] ||
        !!taxAmountDetailError["0.08"];

  const handleOnFetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const aplAndProc = await runGqlQueryV2(() =>
        gqlClient.applicationAndProcedure({ id: id }, gqlHeader("q")),
      );
      if (aplAndProc.error) {
        throw aplAndProc.error;
      }
      if (!aplAndProc.data) {
        throw new Error("データが取得できませんでした。");
      }

      const application = ApplicationEntity.fromJSON(
        aplAndProc.data.applicationDetailData.application as ApplicationType,
      );
      const procedure = ProcedureEntity.fromJSON(
        aplAndProc.data?.applicationDetailData.procedure as ProcedureType,
      );

      const invoiceCreationData = await runGqlQueryV2(() =>
        gqlClient.getNewInvoiceCreationData(
          { applicationId: id },
          gqlHeader("q"),
        ),
      );
      if (invoiceCreationData.error) {
        throw invoiceCreationData.error;
      }
      if (!invoiceCreationData.data) {
        throw new Error("データが取得できませんでした。");
      }
      const { qualifiedInvoiceIssueRequest, qualifiedInvoiceDetailPayment } =
        invoiceCreationData.data;
      if (qualifiedInvoiceIssueRequest && qualifiedInvoiceDetailPayment) {
        setProcedureId(procedure.id);
        setProcedureGroupId(procedure.groupIds[0]);
        const applicantName =
          application.getApplicant(procedure.published!.applicantSchemaKeys)
            .name + qualifiedInvoiceBuyerNameDefaultSuffix;
        setBuyerName(applicantName);
        setBuyerNameApplicantName(applicantName);

        setIssueRequest(qualifiedInvoiceIssueRequest);
        setDetailPayment(qualifiedInvoiceDetailPayment);
        setAmountDetail(qualifiedInvoiceDetailPayment.amountDetail);
        setTaxAmountDetail(qualifiedInvoiceDetailPayment.taxAmountDetail);
      }
    } catch (e) {
      toast.error(
        "支払い明細書の交付の依頼の取得に失敗しました。時間をおいてから、再度お試しください。",
      );
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const handleOnIssue = async ({
    applicationId,
    qualifiedInvoiceIssueRequestId,
  }: {
    applicationId: string;
    qualifiedInvoiceIssueRequestId: string;
  }) => {
    if (
      !window.confirm(
        "入力された内容で支払い明細書を交付します。よろしいですか？",
      )
    ) {
      return;
    }

    const input: IssueQualifiedInvoiceInput = {
      applicationId,
      qualifiedInvoiceIssueRequestId,
      transactionDate: {
        year: transactionDate.getFullYear(),
        month: transactionDate.getMonth() + 1,
        day: transactionDate.getDate(),
      },
    };

    if (format === "STANDARD") {
      input.buyerName = buyerName;

      if (amountDetail["0.1"]! > 0 && taxAmountDetail["0.1"]! >= 0) {
        input.defaultTaxAmount = taxAmountDetail["0.1"];
      }

      if (amountDetail["0.08"]! > 0 && taxAmountDetail["0.08"]! >= 0) {
        input.reducedTaxAmount = taxAmountDetail["0.08"];
      }
    }

    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.issueQualifiedInvoice(
          {
            input,
          },
          gqlHeader("m"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data?.issueQualifiedInvoice) {
        toast.success("インボイスを交付しました。");
        handleOnRedirectToApplicationDetail();
      }
    } catch (e) {
      toast.error(
        "インボイスの交付に失敗しました。時間をおいて再度お試しください。",
      );
      reportUnexpectedError(e);
    }
  };

  useEffect(() => {
    handleOnFetch();
  }, [handleOnFetch]);

  return {
    isLoading,
    redirectTo,
    procedureId,
    procedureGroupId,
    issueRequest,
    detailPayment,
    amountDetail,
    format,
    transactionDate,
    transactionDateError,
    buyerName,
    buyerNameApplicantName,
    buyerNameError,
    taxAmountDetail,
    taxAmountDetailError,
    disabledIssueButton,
    handleOnRedirectToApplicationDetail,
    handleOnChangeFormat,
    handleOnChangeTransactionDate,
    handleOnChangeBuyerName,
    handleOnChangeTaxAmountDetail,
    handleOnIssue,
  };
};
